// The five editing steps of the application wizard. Each step only edits local
// state; the editor saves the whole fields object (revise_permit_draft).
import { useEffect, useId, useState } from 'react'
import { DateTimeInput } from '@/components/DateTimeInput'
import { ErrorBanner, RuleRefs } from '@/components/evidence'
import { MultiCombobox } from '@/components/MultiCombobox'
import { Banner, Button, Card, Checkbox, EmptyState, Field, RadioGroup, Select, TextArea, TextInput } from '@/components/ui'
import type { ActivityGroup } from '@/domain/catalogue'
import { ACTIVITY_GROUPS, classActivitiesFor, GAS_SPECIES, GAS_TRIGGERS, ISOLATION_KINDS, LOCATION_CLASSIFICATIONS, PERMIT_CLASSES, PRESSURE_TEST_GROUP, PRESSURE_TEST_TYPES, SUPPORTING_DOCUMENT_KINDS } from '@/domain/catalogue'
import type { Blocker, DraftFields, DraftTask, Me, PolicyView, Registry } from '@/domain/types'
import { fmtDateTime, fromLocalInput, toLocalInput } from '@/lib/format'
import { fieldError, FieldErrors, IntegerInput, PolicyNote, Question, RemoveButton, SubHeading, YesNo } from './bits'
import {
  acceptRolesFor,
  emptyHazard,
  emptyTask,
  findParam,
  isolationRouteSupported,
  JOB_READINESS_CONFIRMATIONS,
  JOB_READINESS_QUESTION,
  noticeStatus,
  paramNumber,
  parseHoursInput,
  phCandidates,
  phScopeProblem,
  resequence,
  ROUTE_QUESTIONS,
  speciesLabel,
  taskIsPressureTest,
  taskNeedsProcedure,
  THIRD_PARTY_CONFIRMATIONS,
  THIRD_PARTY_QUESTION,
  toggleIn,
  withActivityGroup,
  withArea,
  withGasRequired,
  withIsolationRequired,
  withPermitClassActivities,
  locationClassificationLabel,
  withRequestedFrom,
  withThirdParty,
  windowSpan,
} from './model'
import { useGuardedCommand } from './hooks'
import { classLabel } from './views'

export type StepProps = {
  fields: DraftFields
  update: (fn: (f: DraftFields) => DraftFields) => void
  registry: Registry
  policy: PolicyView | null
  /** Server blockers from the last submit attempt for one top-level field. */
  errors: (root: string) => Blocker[]
}

/** Text inputs bound to nullable fields store null when cleared, otherwise exactly what was typed. */
const orNull = (v: string): string | null => (v === '' ? null : v)

// -----------------------------------------------------------------------------
// Step 1: geographical area (site), unit (site area), class, class activities, work type
// -----------------------------------------------------------------------------
const ADD_SITE = '__add_site__'
const ADD_UNIT = '__add_unit__'

/** The fixed work-location classifications, the non-hydrocarbon ones in their own group. */
function ClassificationSelect({ id, value, onChange }: { id: string; value: string; onChange: (v: string) => void }) {
  return (
    <select
      id={id}
      className="w-full min-h-[46px] rounded-lg border border-line-strong bg-white px-3 py-2 leading-normal text-ink"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">Choose the work-location classification</option>
      {LOCATION_CLASSIFICATIONS.filter((c) => c.group === null).map((c) => (
        <option key={c.code} value={c.code}>
          {c.option}
        </option>
      ))}
      <optgroup label="Non-hydrocarbon areas">
        {LOCATION_CLASSIFICATIONS.filter((c) => c.group === 'Non-hydrocarbon areas').map((c) => (
          <option key={c.code} value={c.code}>
            {c.option}
          </option>
        ))}
      </optgroup>
    </select>
  )
}

/**
 * Adds a geographical area with its first unit (add_site), or a unit to the chosen
 * geographical area (add_unit). The server checks the grant, the names and the list;
 * a new geographical area inherits the issuing authority and access of the one it is
 * added under. Saving a registry entry signs nothing.
 */
function AddLocationForm({ kind, registry, me, siteId, onAdded, onCancel }: {
  kind: 'site' | 'unit'
  registry: Registry
  me: Me | null
  siteId: string | null
  onAdded: (siteId: string, unitId: string) => void
  onCancel: () => void
}) {
  const id = useId()
  const canApply = (sid: string) => Boolean(me?.sites.find((s) => s.id === sid)?.capabilities.includes('apply_permits'))
  const parents = registry.sites.filter((s) => canApply(s.id))
  const [parentId, setParentId] = useState<string>(() => (siteId && canApply(siteId) ? siteId : (parents[0]?.id ?? '')))
  const [siteName, setSiteName] = useState('')
  const [unitName, setUnitName] = useState('')
  const [classification, setClassification] = useState('')
  const addSite = useGuardedCommand('add_site')
  const addUnit = useGuardedCommand('add_unit')
  const cmd = kind === 'site' ? addSite : addUnit
  const err = cmd.error
  const missing: string[] = []
  if (kind === 'site' && siteName.trim().length < 2) missing.push('the geographical area name')
  if (unitName.trim().length < 2) missing.push('the unit name')
  if (!classification) missing.push("the unit's work-location classification")
  if (kind === 'site' && !parentId) missing.push('the geographical area it is added under')
  const site = registry.sites.find((s) => s.id === siteId)
  const save = async () => {
    try {
      if (kind === 'site') {
        const r = await addSite.run({ parent_site_id: parentId, site_name: siteName, unit_name: unitName, location_classification: classification || null })
        onAdded(String(r.site_id), String(r.site_area_id))
      } else if (siteId) {
        const r = await addUnit.run({ site_id: siteId, unit_name: unitName, location_classification: classification || null })
        onAdded(siteId, String(r.site_area_id))
      }
    } catch {
      // Shown below from the command's error.
    }
  }
  return (
    <div className="mb-4 rounded-lg border-2 border-line-strong bg-paper p-3">
      <p className="mb-2 text-sm font-semibold text-ink">{kind === 'site' ? 'Add a new geographical area' : `Add a new unit to ${site?.name ?? 'this geographical area'}`}</p>
      {kind === 'site' ? (
        <>
          <Field label="Geographical area name" required htmlFor={`${id}-sname`} hint="2 to 80 characters, for example Adam Test Site.">
            <TextInput id={`${id}-sname`} value={siteName} maxLength={80} onChange={(e) => setSiteName(e.target.value)} />
          </Field>
          <Field
            label="Added under"
            required
            htmlFor={`${id}-parent`}
            hint="The new geographical area takes this one's issuing authority and rules, and the same people can see it and apply there."
          >
            <Select id={`${id}-parent`} value={parentId} options={parents.map((s) => ({ value: s.id, label: s.name }))} onChange={(e) => setParentId(e.target.value)} />
          </Field>
        </>
      ) : null}
      <Field label={kind === 'site' ? 'First unit name' : 'Unit name'} required htmlFor={`${id}-uname`} hint="2 to 80 characters, for example OTSG unit or Workshop.">
        <TextInput id={`${id}-uname`} value={unitName} maxLength={80} onChange={(e) => setUnitName(e.target.value)} />
      </Field>
      <Field label="Unit's work-location classification" required htmlFor={`${id}-uclass`} hint="From the approved site record. Every permit in this unit uses it.">
        <ClassificationSelect id={`${id}-uclass`} value={classification} onChange={setClassification} />
      </Field>
      <p className="mb-3 text-xs text-ink-soft">
        Licences are not extended by adding a location: the people who work here need licences that cover the new unit, recorded separately.
      </p>
      {missing.length > 0 ? <p className="mb-2 text-sm text-ink">Still needed: {missing.join('; ')}.</p> : null}
      <ErrorBanner error={err} />
      <div className="mt-2 flex flex-wrap justify-end gap-2">
        <Button variant="secondary" type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" loading={cmd.isPending} disabled={missing.length > 0} onClick={() => void save()}>
          {kind === 'site' ? 'Save the geographical area' : 'Save the unit'}
        </Button>
      </div>
    </div>
  )
}

export function StepScope({ fields, update, registry, errors, isNew, siteId, siteName, onSite, permitClass, onClass, me }: StepProps & {
  isNew: boolean
  siteId: string | null
  siteName: string | null
  onSite: (id: string | null) => void
  permitClass: 'A' | 'B' | null
  onClass: (c: 'A' | 'B' | null) => void
  me: Me | null
}) {
  const id = useId()
  const [adding, setAdding] = useState<'site' | 'unit' | null>(null)
  const [classNote, setClassNote] = useState<string | null>(null)
  const site = registry.sites.find((s) => s.id === siteId) ?? null
  const area = site?.areas.find((a) => a.id === fields.site_area_id) ?? null
  const caps = me?.sites.find((s) => s.id === siteId)?.capabilities ?? null
  const canAddSite = Boolean(me?.sites.some((s) => s.capabilities.includes('apply_permits')))
  const canAddUnit = Boolean(caps?.includes('apply_permits'))
  const ownIssue = site?.authority?.authority_mode === 'own_issue'
  const activityOptions = classActivitiesFor(permitClass).map((a) => ({ value: a.code, label: a.label, group: a.group, keywords: `SP-2329 page ${a.page}` }))
  const onAdded = (newSite: string, newUnit: string) => {
    setAdding(null)
    if (newSite !== siteId) onSite(newSite)
    update((f) => withArea({ ...f, affected_area_ids: newSite !== siteId ? [] : f.affected_area_ids }, newUnit))
  }
  const unitLabel = area ? (area.location_classification_label ?? locationClassificationLabel(area.location_classification)) : null
  return (
    <div>
      <Field
        label="Geographical area"
        required
        htmlFor={isNew ? `${id}-site` : undefined}
        hint={isNew ? 'For example Adam Test Site. Not listed? Choose "Add a new geographical area".' : 'The geographical area is fixed once the draft exists.'}
        error={fieldError(errors('site_id'))}
      >
        {isNew ? (
          <Select
            id={`${id}-site`}
            value={adding === 'site' ? ADD_SITE : (siteId ?? '')}
            placeholder="Choose the geographical area"
            options={[
              ...registry.sites.map((s) => ({ value: s.id, label: s.name })),
              ...(canAddSite ? [{ value: ADD_SITE, label: 'Add a new geographical area...' }] : []),
            ]}
            onChange={(e) => {
              if (e.target.value === ADD_SITE) setAdding('site')
              else {
                setAdding(null)
                onSite(e.target.value || null)
              }
            }}
          />
        ) : (
          <p className="text-ink">{siteName ?? 'Unknown geographical area'}</p>
        )}
      </Field>
      {adding === 'site' ? <AddLocationForm kind="site" registry={registry} me={me} siteId={siteId} onAdded={onAdded} onCancel={() => setAdding(null)} /> : null}
      {site && !ownIssue ? (
        <Banner tone="bad" title="BadrEOR cannot issue its own permits in this geographical area" className="mb-4">
          {site.authority
            ? "This geographical area is governed by the client's permit system. BadrEOR can only record those permits, not issue them."
            : 'No current BadrEOR issuing authority covers this geographical area.'}
          <RuleRefs ruleIds={['SYS-002']} decisionIds={['DEC-SYS-002']} />
        </Banner>
      ) : null}
      {site && caps && !caps.includes('apply_permits') ? (
        <Banner tone="warn" className="mb-4">
          Your account has no grant to apply for permits in this geographical area. The server will refuse to save the draft.
        </Banner>
      ) : null}

      <Question legend="Permit class" required hint="Class A can be raised only where the rules in use enable it; the server says so if they do not." errors={errors('permit_class')}>
        {isNew ? (
          <RadioGroup
            name={`${id}-class`}
            value={permitClass}
            onChange={(v) => {
              const next = v === 'A' || v === 'B' ? v : null
              const r = withPermitClassActivities(fields, next)
              if (r.removed.length) {
                update(() => r.fields)
                setClassNote(`Removed because they are not activities of this class: ${r.removed.join('; ')}.`)
              } else setClassNote(null)
              onClass(next)
            }}
            options={PERMIT_CLASSES.map((c) => ({
              value: c.code,
              disabled: c.code !== 'A' && c.code !== 'B',
              label: c.code === 'A' || c.code === 'B' ? c.label : `${c.label} - not built yet`,
              hint: (
                <>
                  {c.help}
                  <RuleRefs ruleIds={c.rule_ids ? [...c.rule_ids] : []} />
                </>
              ),
            }))}
          />
        ) : (
          <p className="text-ink">{classLabel(permitClass)}</p>
        )}
      </Question>
      <Banner tone="info" className="mb-4">
        Task SOP / Notification of Work and No Permit Job are separate routes that are not built yet. If the work needs one of them, it
        cannot be raised in this release. Do not raise it as Class B instead. Raise Class A work as Class A, not as Class B: hot work,
        confined-space entry and isolation still have no route, so Class A work that needs them stays blocked.
      </Banner>

      <Field
        label="Why this class: the activities in this work"
        required
        htmlFor={`${id}-acts`}
        hint={
          permitClass
            ? `Type to search the SP-2329 Appendix 1 activities listed for Class ${permitClass} and pick every one this work includes. The list describes the work; it never sets the class or any limit (CORE-017, CORE-022).`
            : 'Choose the permit class first; then pick the activities listed for it.'
        }
        error={fieldError(errors('class_activities'))}
      >
        <MultiCombobox
          id={`${id}-acts`}
          options={activityOptions}
          value={fields.class_activities}
          disabled={!permitClass}
          placeholder={permitClass ? 'Search, e.g. painting, scaffold, 6.4' : 'Choose the permit class first'}
          onChange={(next) => update((f) => ({ ...f, class_activities: next }))}
        />
      </Field>
      {classNote ? (
        <Banner tone="warn" className="mb-4">
          {classNote}
        </Banner>
      ) : null}
      {fields.class_basis.trim() ? (
        <p className="-mt-2 mb-4 text-xs text-ink-soft">
          Earlier free-text class basis (kept, not asked any more): {fields.class_basis}
        </p>
      ) : null}

      <Field label="Unit" required htmlFor={`${id}-area`} hint="For example OTSG unit, Water Treatment Plant or Workshop." error={fieldError(errors('site_area_id'))}>
        <Select
          id={`${id}-area`}
          value={adding === 'unit' ? ADD_UNIT : (fields.site_area_id ?? '')}
          disabled={!site}
          placeholder={site ? 'Choose the unit' : 'Choose the geographical area first'}
          options={[
            ...(site?.areas ?? []).map((a) => ({ value: a.id, label: `${a.name} (${a.code})` })),
            ...(site && canAddUnit ? [{ value: ADD_UNIT, label: 'Add a new unit...' }] : []),
          ]}
          onChange={(e) => {
            if (e.target.value === ADD_UNIT) setAdding('unit')
            else {
              setAdding(null)
              update((f) => withArea(f, e.target.value || null))
            }
          }}
        />
      </Field>
      {adding === 'unit' ? <AddLocationForm kind="unit" registry={registry} me={me} siteId={siteId} onAdded={onAdded} onCancel={() => setAdding(null)} /> : null}
      {area ? (
        <div className="-mt-2 mb-4 rounded-lg border border-line bg-paper p-3 text-sm">
          <p>
            <span className="font-semibold">Unit's work-location classification: </span>
            {unitLabel ?? <span className="font-semibold text-bad">Not recorded in the register - a permit cannot be submitted here until it is.</span>}
          </p>
          <p className="mt-1 text-xs text-ink-soft">From the register, sealed with the application. You confirm it in step 5. Changing the unit clears that confirmation.</p>
        </div>
      ) : null}

      <Field
        label="Exact location in the unit"
        required
        htmlFor={`${id}-loc`}
        hint="For example the line, grid reference or equipment tags."
        error={fieldError(errors('location_detail'))}
      >
        <TextArea id={`${id}-loc`} rows={2} value={fields.location_detail} onChange={(e) => update((f) => ({ ...f, location_detail: e.target.value }))} />
      </Field>

      <Field
        label="Work type"
        required
        htmlFor={`${id}-wt`}
        hint="A work type names the business scope. It never sets the permit class or an exemption."
        error={fieldError(errors('work_type_id'))}
      >
        <Select
          id={`${id}-wt`}
          value={fields.work_type_id ?? ''}
          placeholder="Choose the work type"
          options={registry.work_types.map((w) => ({ value: w.id, label: `${w.description} - ${w.activity_group}` }))}
          onChange={(e) => update((f) => ({ ...f, work_type_id: e.target.value || null }))}
        />
      </Field>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Step 2: the work
// -----------------------------------------------------------------------------
export function StepWork({ fields, update, registry, policy, errors, nowMs, me }: StepProps & { nowMs: number; me: Me | null }) {
  const id = useId()
  const noticeParam = findParam(policy, 'class_b.notice_min_hours')
  const noticeHours = paramNumber(noticeParam)
  const maxDaysParam = findParam(policy, 'class_b.authorisation_max_days')
  const maxDays = paramNumber(maxDaysParam)
  const level2Param = findParam(policy, 'work_party.level2_trigger_persons')
  const level2 = paramNumber(level2Param)
  const notice = noticeStatus(fields.requested_from, nowMs, noticeHours)
  // Asked only when the start is sooner than the notice (CORE-016); the server requires it only then.
  // (or when the server asked for it at the last submit attempt).
  const showUrgency = notice === 'short' || errors('urgency_justification').length > 0
  const span = windowSpan(fields.requested_from, fields.requested_to)

  const phRoles = acceptRolesFor(policy, 'PH')
  const phParam = findParam(policy, 'role_routes')
  const candidates = phCandidates(registry.people, phRoles)
  const scopeProblem = (p: Registry['people'][number] | null | undefined) => phScopeProblem(p, phRoles, fields.site_area_id, fields.work_type_id)
  const phOptions = candidates.map((p) => ({
    value: p.id,
    label: `${p.display_name}${p.organisation ? ` - ${p.organisation}` : ''} (${p.roles.join(', ')})${scopeProblem(p) ? ' - licence may not cover this work' : ''}`,
  }))
  const chosen = fields.nominated_ph_person_id
  if (chosen && !candidates.some((p) => p.id === chosen)) {
    const p = registry.people.find((x) => x.id === chosen)
    phOptions.push({ value: chosen, label: `${p?.display_name ?? 'Unknown person'} - not listed as able to act as Permit Holder` })
  }
  const chosenProblem = chosen ? scopeProblem(registry.people.find((x) => x.id === chosen)) : null
  const tp = fields.third_party

  return (
    <div>
      <Field label="Short title" required htmlFor={`${id}-title`} hint="What people on site would call this job." error={fieldError(errors('title'))}>
        <TextInput id={`${id}-title`} value={fields.title} onChange={(e) => update((f) => ({ ...f, title: e.target.value }))} />
      </Field>
      <Field label="Work order reference" required htmlFor={`${id}-wo`} hint="The work order this job is done under (CORE-028)." error={fieldError(errors('work_order_ref'))}>
        <TextInput id={`${id}-wo`} value={fields.work_order_ref ?? ''} onChange={(e) => update((f) => ({ ...f, work_order_ref: orNull(e.target.value) }))} />
      </Field>

      <div className="mb-4 rounded-lg border border-line bg-paper p-3 text-sm">
        <p>
          <span className="font-semibold">Date of application: </span>
          Recorded when you submit (the server's time).
        </p>
        <p className="mt-1">
          <span className="font-semibold">Date of initiation: </span>
          {fields.requested_from ? fmtDateTime(fields.requested_from) : 'the planned start you enter below.'}
        </p>
        {noticeHours !== null ? (
          <PolicyNote param={noticeParam}>
            Class B needs {noticeHours} hours' notice: plan the start at least {noticeHours} hours after you submit (CORE-009, CORE-016).
          </PolicyNote>
        ) : null}
      </div>

      <div className="grid gap-x-4 sm:grid-cols-2">
        <Field label="Planned start (date of initiation)" required htmlFor={`${id}-from`} error={fieldError(errors('requested_from'))}>
          <DateTimeInput
            id={`${id}-from`}
            name="Planned start"
            value={toLocalInput(fields.requested_from)}
            onChange={(v) => {
              const fromIso = fromLocalInput(v)
              update((f) => withRequestedFrom(f, fromIso, { maxDays, noticeOk: noticeStatus(fromIso, nowMs, noticeHours) === 'ok' }))
            }}
          />
        </Field>
        <Field
          label="Planned end"
          required
          htmlFor={`${id}-to`}
          hint={maxDays !== null ? `Filled in with the start plus ${maxDays} days. Bring it forward to when the work will really finish.` : undefined}
          error={fieldError(errors('requested_to'))}
        >
          <DateTimeInput
            id={`${id}-to`}
            name="Planned end"
            value={toLocalInput(fields.requested_to)}
            onChange={(v) => update((f) => ({ ...f, requested_to: fromLocalInput(v) }))}
          />
        </Field>
      </div>
      {span ? (
        <p className={span.ms > 0 ? 'mb-1 text-sm text-ink' : 'mb-1 text-sm font-semibold text-bad'}>
          {span.ms > 0 ? `Planned length: ${span.text}.` : '! The end must be after the start.'}
          {span.ms > 0 && maxDays !== null && span.ms > maxDays * 86_400_000 ? (
            <span className="block font-semibold text-bad">! This is longer than the {maxDays}-day Class B maximum.</span>
          ) : null}
        </p>
      ) : null}
      {maxDays !== null ? (
        <PolicyNote param={maxDaysParam}>
          {maxDays} days is the Class B maximum, not a default duration: the authorisation should fit the work (CORE-028).
        </PolicyNote>
      ) : null}

      {showUrgency ? (
        <div className="mt-4">
          <Field
            label="Why the work cannot wait (urgency justification)"
            required
            htmlFor={`${id}-urg`}
            error={fieldError(errors('urgency_justification'))}
            hint={`Asked because the planned start is less than ${noticeHours ?? 'the required'} hours away, sooner than the Class B notice. Explain why it is urgent and any particular risks; the permit meeting decides whether to accept it (CORE-016).`}
          >
            <TextArea id={`${id}-urg`} value={fields.urgency_justification ?? ''} onChange={(e) => update((f) => ({ ...f, urgency_justification: orNull(e.target.value) }))} />
          </Field>
        </div>
      ) : null}

      <div className="mt-4">
        <Field label="What will be done" required htmlFor={`${id}-desc`} error={fieldError(errors('work_description'))}>
          <TextArea id={`${id}-desc`} value={fields.work_description} onChange={(e) => update((f) => ({ ...f, work_description: e.target.value }))} />
        </Field>
        <Field
          label="Work boundary"
          required
          htmlFor={`${id}-bound`}
          hint="Where the work starts and stops: what is in scope and what is not."
          error={fieldError(errors('work_boundary'))}
        >
          <TextArea id={`${id}-bound`} value={fields.work_boundary} onChange={(e) => update((f) => ({ ...f, work_boundary: e.target.value }))} />
        </Field>
        <Field label="Tools, equipment and vehicles" required htmlFor={`${id}-tools`} error={fieldError(errors('tools_equipment'))}>
          <TextArea id={`${id}-tools`} value={fields.tools_equipment} onChange={(e) => update((f) => ({ ...f, tools_equipment: e.target.value }))} />
        </Field>
        <Field
          label="Most people in the work party at one time"
          required
          htmlFor={`${id}-party`}
          hint="Count everyone working under this permit at the same time."
          error={fieldError(errors('work_party_max'))}
        >
          <IntegerInput id={`${id}-party`} min={1} value={fields.work_party_max} onChange={(v) => update((f) => ({ ...f, work_party_max: v }))} />
          {level2 !== null ? <PolicyNote param={level2Param}>More than {level2} people needs a Level 2 risk assessment.</PolicyNote> : null}
        </Field>
        {level2 !== null && fields.work_party_max !== null && fields.work_party_max > level2 ? (
          <Banner tone="bad" className="mb-4" title="Level 2 risk assessment needed">
            A work party above {level2} people needs a Level 2 risk assessment, which is not built in this release. This application cannot be
            submitted as it stands.
            <RuleRefs ruleIds={['CORE-029', 'CORE-034']} decisionIds={['DEC-CORE-004']} />
          </Banner>
        ) : null}

        <Field
          label="Nominated Permit Holder"
          required
          htmlFor={`${id}-ph`}
          hint="The person who will hold the permit at the worksite. Their qualification for this area is checked when you submit."
          error={fieldError(errors('nominated_ph_person_id'))}
        >
          <Select
            id={`${id}-ph`}
            value={chosen ?? ''}
            placeholder="Choose the Permit Holder"
            options={phOptions}
            onChange={(e) => update((f) => ({ ...f, nominated_ph_person_id: e.target.value || null }))}
          />
          {phRoles ? (
            <PolicyNote param={phParam}>Listed: people whose licence may act as Permit Holder ({phRoles.join(', ')}).</PolicyNote>
          ) : (
            <p className="mt-1 text-xs text-ink-soft">Listed: Permit Holder licences only, because the role rules could not be loaded.</p>
          )}
        </Field>
        {chosen && me && chosen === me.person.id ? (
          <Banner tone="warn" className="mb-4">
            You are the applicant. The same person cannot be both applicant and Permit Holder on one permit in this release.
            <RuleRefs ruleIds={['CORE-075']} />
          </Banner>
        ) : null}
        {chosenProblem ? (
          <Banner tone="warn" className="mb-4" title="This Permit Holder's licence may not cover this work">
            {chosenProblem} Submitting will be refused (CREDENTIAL_OUT_OF_SCOPE) unless the licence covers the unit and work type. Choose another Permit
            Holder, or ask for the licence scope to be updated. The server checks the licence again when you submit.
            <RuleRefs ruleIds={['CORE-057']} />
          </Banner>
        ) : null}

        <Question
          legend={THIRD_PARTY_QUESTION}
          required
          hint="Any company other than BadrEOR doing part of this work, including subcontractors and vendors."
          errors={errors('third_party')}
        >
          <YesNo name={`${id}-tp`} value={tp.involved} onChange={(v) => update((f) => withThirdParty(f, v))} />
          {tp.involved === false ? <p className="mt-1 text-xs text-ink-soft">Answering No clears the confirmations below.</p> : null}
        </Question>
        {tp.involved === true ? (
          <Question legend="Confirm for the third party or subcontractor" required hint="Tick each one only when you have seen the approval (GOV-004).">
            <div className="divide-y divide-line rounded-lg border border-line bg-white px-3">
              {THIRD_PARTY_CONFIRMATIONS.map((c) => (
                <Checkbox
                  key={c.key}
                  checked={tp[c.key]}
                  onChange={(v) => update((f) => ({ ...f, third_party: { ...f.third_party, [c.key]: v } }))}
                  label={c.label}
                />
              ))}
            </div>
          </Question>
        ) : null}
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Step 3: tasks
// -----------------------------------------------------------------------------
function ProcedureEditor({ task, onChange }: { task: DraftTask; onChange: (t: DraftTask) => void }) {
  const id = useId()
  const verified = taskNeedsProcedure(task)
  const ref = task.applicable_procedure_reference
  const setRef = (patch: Partial<{ identity: string; revision: string; issuer: string }>) => {
    const next = { identity: ref?.identity ?? '', revision: ref?.revision ?? '', issuer: ref?.issuer ?? '', ...patch }
    onChange({ ...task, applicable_procedure_reference: next.identity || next.revision || next.issuer ? next : null })
  }
  return (
    <div className="rounded-lg border border-line bg-paper p-3">
      <p className="text-sm font-semibold text-ink">
        Procedure<span className="ml-1 text-bad">*</span>
      </p>
      <p className="mb-3 text-xs text-ink-soft">
        Every task needs the procedure it follows.
        {verified
          ? ' For pressure tests and Surface Preparation it must also be verified before authorisation: a verified supporting document with exactly the same identifier, revision and issuer.'
          : ''}
      </p>
      <div className="grid gap-x-3 sm:grid-cols-3">
        <Field label="Document identifier" required htmlFor={`${id}-pid`}>
          <TextInput id={`${id}-pid`} value={ref?.identity ?? ''} onChange={(e) => setRef({ identity: e.target.value })} />
        </Field>
        <Field label="Revision" required htmlFor={`${id}-prev`}>
          <TextInput id={`${id}-prev`} value={ref?.revision ?? ''} onChange={(e) => setRef({ revision: e.target.value })} />
        </Field>
        <Field label="Issued by" required htmlFor={`${id}-piss`}>
          <TextInput id={`${id}-piss`} value={ref?.issuer ?? ''} onChange={(e) => setRef({ issuer: e.target.value })} />
        </Field>
      </div>
    </div>
  )
}

function TaskEditor({ task, onChange, onRemove }: {
  task: DraftTask
  onChange: (t: DraftTask) => void
  onRemove: () => void
}) {
  const id = useId()
  const pressureTest = taskIsPressureTest(task)
  // A type or medium kept from an earlier draft under another group is shown so it can be cleared.
  const strayTest = !pressureTest && (task.pressure_test_type !== null || task.test_medium !== null)
  return (
    <Card className="mb-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="display text-lg font-semibold text-ink">Task {task.sequence}</h3>
        <RemoveButton label="Remove task" onConfirm={onRemove} />
      </div>
      <Field
        label="Equipment worked on"
        htmlFor={`${id}-eq`}
        hint="Type the equipment tag and a short description, for example SYN-PS-101 pipe support. The maintenance register is not complete, so it is not a pick-list."
      >
        <TextInput id={`${id}-eq`} value={task.equipment_text ?? ''} onChange={(e) => onChange({ ...task, equipment_text: orNull(e.target.value) })} />
      </Field>
      <Field label="What is done in this task" required htmlFor={`${id}-desc`}>
        <TextArea id={`${id}-desc`} value={task.description} onChange={(e) => onChange({ ...task, description: e.target.value })} />
      </Field>
      <Field
        label="Activity group"
        required
        htmlFor={`${id}-group`}
        hint="Pressure Testing asks for the test type and medium; Surface Preparation asks for the method. Changing the group clears details of the old one."
      >
        <Select
          id={`${id}-group`}
          value={task.activity_group ?? ''}
          placeholder="Choose the activity group"
          options={ACTIVITY_GROUPS.map((g) => ({ value: g, label: g }))}
          onChange={(e) => onChange(withActivityGroup(task, (e.target.value || null) as ActivityGroup | null))}
        />
      </Field>
      {task.activity_group === 'Surface Preparation' ? (
        <Field
          label="Actual surface preparation method"
          required
          htmlFor={`${id}-spm`}
          hint="Describe the real method, tools and material. Nothing is assumed from the group name (ACT-069)."
        >
          <TextArea id={`${id}-spm`} value={task.surface_preparation_method ?? ''} onChange={(e) => onChange({ ...task, surface_preparation_method: orNull(e.target.value) })} />
        </Field>
      ) : null}

      {pressureTest || strayTest ? (
        <div className="mb-4 rounded-lg border border-line bg-paper p-3">
          {strayTest ? (
            <div className="mb-3">
              <p className="text-xs font-semibold text-bad">
                ! A pressure-test type or medium is recorded, but the activity group is not {PRESSURE_TEST_GROUP}. Choose {PRESSURE_TEST_GROUP} or clear them.
              </p>
              <Button variant="secondary" type="button" className="mt-2" onClick={() => onChange({ ...task, pressure_test_type: null, test_medium: null })}>
                Clear the pressure-test details
              </Button>
            </div>
          ) : (
            <p className="mb-2 text-xs text-ink-soft">Asked because the activity group is {PRESSURE_TEST_GROUP} (ACT-070).</p>
          )}
          <Question legend="Pressure test type" required hint="Three separate types. Hydraulic is not the same as Hydrostatic, and no type sets a medium or limit (ACT-070).">
            <RadioGroup
              name={`${id}-ptt`}
              value={task.pressure_test_type}
              onChange={(v) => onChange({ ...task, pressure_test_type: v })}
              options={PRESSURE_TEST_TYPES.map((p) => ({ value: p, label: p }))}
            />
          </Question>
          <Field label="Actual test medium" required htmlFor={`${id}-med`} hint="State the medium actually used. It is never filled in from the test type.">
            <TextInput id={`${id}-med`} value={task.test_medium ?? ''} onChange={(e) => onChange({ ...task, test_medium: orNull(e.target.value) })} />
          </Field>
        </div>
      ) : null}
      <ProcedureEditor task={task} onChange={onChange} />
    </Card>
  )
}

export function StepTasks({ fields, update, errors }: StepProps) {
  const setTask = (i: number, t: DraftTask) => update((f) => ({ ...f, tasks: f.tasks.map((x, j) => (j === i ? t : x)) }))
  return (
    <div>
      <p className="mb-3 text-sm text-ink-soft">
        Break the work into tasks. Each task records the equipment worked on, what is done, its activity group and the procedure it follows.
      </p>
      <Banner tone="info" title="Labels never set a permit class or limits" className="mb-4">
        Choosing an activity group or a pressure-test type records what the task is and which details are asked. It does not set the permit class,
        pressures, hold times, distances or any other limit.
        <RuleRefs ruleIds={['ACT-069', 'ACT-070']} decisionIds={['DEC-ACT-004']} />
      </Banner>
      <FieldErrors errors={errors('tasks')} />
      {fields.tasks.length === 0 ? (
        <div className="mb-4">
          <EmptyState title="No tasks yet">Add at least one task.</EmptyState>
        </div>
      ) : null}
      {fields.tasks.map((t, i) => (
        <TaskEditor
          key={i}
          task={t}
          onChange={(next) => setTask(i, next)}
          onRemove={() => update((f) => ({ ...f, tasks: resequence(f.tasks.filter((_, j) => j !== i)) }))}
        />
      ))}
      <Button type="button" variant="secondary" onClick={() => update((f) => ({ ...f, tasks: [...f.tasks, emptyTask(f.tasks.length + 1)] }))}>
        Add a task
      </Button>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Step 4: hazards, JHSEP, SIMOPS
// -----------------------------------------------------------------------------
export function StepHazards({ fields, update, policy, errors }: StepProps) {
  const id = useId()
  const level2Param = findParam(policy, 'work_party.level2_trigger_persons')
  const level2 = paramNumber(level2Param)
  const setJ = (patch: Partial<DraftFields['jhsep']>) => update((f) => ({ ...f, jhsep: { ...f.jhsep, ...patch } }))
  const setS = (patch: Partial<DraftFields['simops']>) => update((f) => ({ ...f, simops: { ...f.simops, ...patch } }))
  return (
    <div>
      <SubHeading>Hazards and controls</SubHeading>
      <p className="mb-3 text-sm text-ink-soft">List each hazard with the control that manages it. At least one is needed.</p>
      <FieldErrors errors={errors('hazards')} />
      {fields.hazards.map((h, i) => (
        <Card key={i} className="mb-3">
          <Field label={`Hazard ${i + 1}`} required htmlFor={`${id}-hz-${i}`}>
            <TextArea
              id={`${id}-hz-${i}`}
              rows={2}
              value={h.hazard}
              onChange={(e) => update((f) => ({ ...f, hazards: f.hazards.map((x, j) => (j === i ? { ...x, hazard: e.target.value } : x)) }))}
            />
          </Field>
          <Field label="Control" required htmlFor={`${id}-ct-${i}`}>
            <TextArea
              id={`${id}-ct-${i}`}
              rows={2}
              value={h.control}
              onChange={(e) => update((f) => ({ ...f, hazards: f.hazards.map((x, j) => (j === i ? { ...x, control: e.target.value } : x)) }))}
            />
          </Field>
          <Question legend="Type of control" required>
            <YesNo
              name={`${id}-kind-${i}`}
              value={h.kind === null ? null : h.kind === 'standard'}
              yesLabel="Standard"
              noLabel="Additional"
              onChange={(standard) =>
                update((f) => ({ ...f, hazards: f.hazards.map((x, j) => (j === i ? { ...x, kind: standard ? 'standard' : 'additional' } : x)) }))
              }
            />
          </Question>
          <RemoveButton label="Remove this hazard" onConfirm={() => update((f) => ({ ...f, hazards: f.hazards.filter((_, j) => j !== i) }))} />
        </Card>
      ))}
      <Button type="button" variant="secondary" onClick={() => update((f) => ({ ...f, hazards: [...f.hazards, emptyHazard()] }))}>
        Add a hazard and control
      </Button>

      <SubHeading>Job HSE plan (JHSEP)</SubHeading>
      <FieldErrors errors={errors('jhsep')} />
      <Question legend="JHSEP level" required>
        <RadioGroup
          name={`${id}-level`}
          value={fields.jhsep.level === null ? null : fields.jhsep.level === 2 ? '2' : '1'}
          onChange={(v) => setJ({ level: v === '2' ? 2 : 1 })}
          options={[
            { value: '1', label: 'Level 1', hint: 'The standard Job HSE plan prepared by the applicant.' },
            {
              value: '2',
              label: 'Level 2 - not built in this release',
              hint: `A team risk assessment under PR-2271, needed ${level2 !== null ? `when the work party is above ${level2} people` : 'for larger work parties'} or when the asset asks for it. It cannot be submitted here.`,
            },
          ]}
        />
      </Question>
      {level2 !== null ? <PolicyNote param={level2Param}>Level 2 trigger: more than {level2} people.</PolicyNote> : null}
      {fields.jhsep.level === 2 ? (
        <Banner tone="bad" title="Level 2 is not supported in this release" className="my-3">
          An application needing a Level 2 risk assessment cannot be submitted. Do not choose Level 1 to get round it.
          <RuleRefs ruleIds={['CORE-034']} decisionIds={['DEC-CORE-004']} />
        </Banner>
      ) : null}
      <Field label="JHSEP reference" required htmlFor={`${id}-jref`}>
        <TextInput id={`${id}-jref`} value={fields.jhsep.reference} onChange={(e) => setJ({ reference: e.target.value })} />
      </Field>
      <Field label="JHSEP summary" required htmlFor={`${id}-jsum`} hint="Main hazards, controls, recovery measures and who could be affected.">
        <TextArea id={`${id}-jsum`} value={fields.jhsep.summary} onChange={(e) => setJ({ summary: e.target.value })} />
      </Field>
      <Question legend="Confirm what you have done" required hint="Tick only what you have actually done (CORE-032).">
        <div className="divide-y divide-line rounded-lg border border-line bg-white px-3">
          <Checkbox checked={fields.jhsep.mopo_reviewed} onChange={(v) => setJ({ mopo_reviewed: v })} label="I reviewed the MOPO for this location" />
          <Checkbox checked={fields.jhsep.lessons_reviewed} onChange={(v) => setJ({ lessons_reviewed: v })} label="I reviewed lessons learned from incidents" />
          <Checkbox
            checked={fields.jhsep.site_examination_done}
            onChange={(v) => setJ({ site_examination_done: v })}
            label="I carried out a site examination to identify hazards"
          />
        </div>
      </Question>
      <Field label="Site examination note" required htmlFor={`${id}-jnote`} hint="When you visited the worksite and what you found (CORE-032).">
        <TextArea id={`${id}-jnote`} rows={2} value={fields.jhsep.site_examination_note ?? ''} onChange={(e) => setJ({ site_examination_note: orNull(e.target.value) })} />
      </Field>

      <SubHeading>Job readiness</SubHeading>
      <Question
        legend={JOB_READINESS_QUESTION}
        required
        hint="Tick each one only when you have checked it yourself for this job. Every one is needed to submit."
        errors={errors('job_readiness')}
      >
        <div className="divide-y divide-line rounded-lg border border-line bg-white px-3">
          {JOB_READINESS_CONFIRMATIONS.map((c) => (
            <Checkbox
              key={c.key}
              checked={fields.job_readiness[c.key]}
              onChange={(v) => update((f) => ({ ...f, job_readiness: { ...f.job_readiness, [c.key]: v } }))}
              label={c.label}
            />
          ))}
        </div>
      </Question>

      <SubHeading>SIMOPS and MOPO</SubHeading>
      <FieldErrors errors={errors('simops')} />
      <Field
        label="Nearby activities"
        htmlFor={`${id}-near`}
        hint="Other work close enough to interfere, clash or transfer risk, including other assets or permit systems (CORE-025)."
      >
        <TextArea id={`${id}-near`} value={fields.simops.nearby_activities} onChange={(e) => setS({ nearby_activities: e.target.value })} />
      </Field>
      <Field label="Conflicts found and how they are managed" htmlFor={`${id}-conf`}>
        <TextArea id={`${id}-conf`} value={fields.simops.conflicts_identified} onChange={(e) => setS({ conflicts_identified: e.target.value })} />
      </Field>
      <div className="rounded-lg border border-line bg-white px-3">
        <Checkbox
          checked={fields.simops.mopo_consulted}
          onChange={(v) => setS({ mopo_consulted: v })}
          label={
            <>
              I consulted the MOPO for simultaneous operations<span className="ml-1 text-bad" aria-hidden>*</span>
            </>
          }
        />
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Step 5: special work, gas plan, documents, affected areas, classification
// -----------------------------------------------------------------------------
export function StepRoutes({ fields, update, registry, policy, errors, siteId }: StepProps & { siteId: string | null }) {
  const id = useId()
  const site = registry.sites.find((s) => s.id === siteId) ?? null
  const area = site?.areas.find((a) => a.id === fields.site_area_id) ?? null
  const otherAreas = (site?.areas ?? []).filter((a) => a.id !== fields.site_area_id)
  const unsupported = findParam(policy, 'routes.unsupported')
  const isolationSupported = isolationRouteSupported(policy)
  const docRoutes = findParam(policy, 'supporting_documents.routes')
  const routedKinds = docRoutes && docRoutes.value_status !== 'unresolved' && typeof docRoutes.value === 'object' && docRoutes.value ? Object.keys(docRoutes.value) : null
  const setG = (patch: Partial<DraftFields['gas_plan']>) => update((f) => ({ ...f, gas_plan: { ...f.gas_plan, ...patch } }))
  const gasErrors = errors('gas_plan')
  return (
    <div>
      <SubHeading>Special work</SubHeading>
      <p className="mb-3 text-sm text-ink-soft">
        Answer each question. A Yes to isolation asks for the isolation details below; a Yes to any other question means the work needs a route that is not built in this release.
      </p>
      {ROUTE_QUESTIONS.map((q) => (
        <Question key={q.key} legend={q.question} hint={q.hint} required errors={errors(q.key)}>
          <YesNo
            name={`${id}-${q.key}`}
            value={fields[q.key]}
            onChange={(v) => update((f) => (q.key === 'isolation_required' ? withIsolationRequired(f, v) : { ...f, [q.key]: v }))}
          />
          {fields[q.key] === true && (q.key !== 'isolation_required' || !isolationSupported) ? (
            <Banner tone="bad" title={q.key === 'isolation_required' ? 'Not supported under this rules version' : 'Not supported in this release'} className="mt-2">
              {q.key === 'isolation_required'
                ? 'The rules version of this permit does not record isolation. Rules versions from SYN-0.5 do, and a new sandbox uses the current version. Do not change the answer to get round it.'
                : 'This application cannot be submitted with this answer. Do not change the answer to get round it: the work needs a route that is not built yet.'}
              <RuleRefs ruleIds={unsupported?.rule_ids ?? ['SYS-016']} decisionIds={unsupported?.decision_ids ?? ['DEC-GOV-010']} />
            </Banner>
          ) : null}
          {q.key === 'isolation_required' && fields.isolation_required === true && isolationSupported ? (
            <IsolationDetailsEditor id={id} fields={fields} update={update} errors={errors} />
          ) : null}
        </Question>
      ))}

      <SubHeading>Gas testing plan</SubHeading>
      <FieldErrors errors={gasErrors} />
      <Question
        legend="Is gas testing required?"
        required
        hint={
          <>
            Gas testing is required for: {GAS_TRIGGERS.map((t) => t.label.toLowerCase()).join('; ')}.
            <RuleRefs ruleIds={['CORE-037']} />
          </>
        }
      >
        <YesNo name={`${id}-gasreq`} value={fields.gas_plan.required} onChange={(v) => update((f) => withGasRequired(f, v))} />
        {fields.gas_plan.required === false ? <p className="mt-1 text-xs text-ink-soft">Answering No clears the reasons, gases and retest interval.</p> : null}
      </Question>
      {fields.gas_plan.required === true ? (
        <>
          <Question legend="Why gas testing is needed" required hint="Tick every reason that applies.">
            <div className="divide-y divide-line rounded-lg border border-line bg-white px-3">
              {GAS_TRIGGERS.map((t) => (
                <Checkbox
                  key={t.code}
                  checked={fields.gas_plan.triggers.includes(t.code)}
                  onChange={(v) => update((f) => ({ ...f, gas_plan: { ...f.gas_plan, triggers: toggleIn(f.gas_plan.triggers, t.code, v) } }))}
                  label={t.label}
                />
              ))}
            </div>
          </Question>
          <Question legend="Gases to test" required>
            <div className="divide-y divide-line rounded-lg border border-line bg-white px-3">
              {GAS_SPECIES.map((s) => (
                <Checkbox
                  key={s.code}
                  checked={fields.gas_plan.species.includes(s.code)}
                  onChange={(v) => update((f) => ({ ...f, gas_plan: { ...f.gas_plan, species: toggleIn(f.gas_plan.species, s.code, v) } }))}
                  label={speciesLabel(GAS_SPECIES, s.code)}
                />
              ))}
            </div>
          </Question>
          <Field
            label="Retest interval in hours"
            required
            htmlFor={`${id}-retest`}
            hint="How often the gas test is repeated while work goes on, in hours (for example 4, or 1.5 for 90 minutes). The RS or AA may shorten it (CORE-038)."
          >
            <HoursInput id={`${id}-retest`} value={fields.gas_plan.retest_interval_hours} onChange={(v) => setG({ retest_interval_hours: v })} />
          </Field>
        </>
      ) : null}
      <Question legend="Is continuous gas monitoring needed?" required>
        <YesNo name={`${id}-cont`} value={fields.gas_plan.continuous_monitoring} onChange={(v) => setG({ continuous_monitoring: v })} />
      </Question>
      <Field label="Gas plan notes" htmlFor={`${id}-gasnotes`} hint="Optional: sample points, who tests, anything the gas tester must know.">
        <TextArea id={`${id}-gasnotes`} rows={2} value={fields.gas_plan.notes} onChange={(e) => setG({ notes: e.target.value })} />
      </Field>
      <p className="mb-2 text-xs text-ink-soft">
        This system sets no gas limits. In this demonstration the gas tester declares each reading against the referenced method.
        <RuleRefs ruleIds={['CORE-044']} decisionIds={['DEC-CORE-005']} />
      </p>

      <SubHeading>Supporting documents</SubHeading>
      <p className="mb-2 text-sm text-ink-soft">List the certificates and documents this work needs, with the reason for each (CORE-033).</p>
      <FieldErrors errors={errors('supporting_documents')} />
      {fields.supporting_documents.map((d, i) => (
        <Card key={i} className="mb-3">
          <Field label={`Document ${i + 1}: kind`} required htmlFor={`${id}-dk-${i}`}>
            <Select
              id={`${id}-dk-${i}`}
              value={d.kind}
              placeholder="Choose the kind"
              options={SUPPORTING_DOCUMENT_KINDS.map((k) => ({ value: k, label: k }))}
              onChange={(e) =>
                update((f) => ({ ...f, supporting_documents: f.supporting_documents.map((x, j) => (j === i ? { ...x, kind: e.target.value } : x)) }))
              }
            />
          </Field>
          <Field label="Why it is needed" htmlFor={`${id}-dr-${i}`}>
            <TextInput
              id={`${id}-dr-${i}`}
              value={d.reason}
              onChange={(e) =>
                update((f) => ({ ...f, supporting_documents: f.supporting_documents.map((x, j) => (j === i ? { ...x, reason: e.target.value } : x)) }))
              }
            />
          </Field>
          {routedKinds && d.kind && !routedKinds.includes(d.kind) ? (
            <p className="mb-3 text-xs font-semibold text-warn">
              ! A {d.kind} cannot be verified in this profile, so authorisation will stay blocked.
            </p>
          ) : null}
          <RemoveButton
            label="Remove this document"
            onConfirm={() => update((f) => ({ ...f, supporting_documents: f.supporting_documents.filter((_, j) => j !== i) }))}
          />
        </Card>
      ))}
      <Button type="button" variant="secondary" onClick={() => update((f) => ({ ...f, supporting_documents: [...f.supporting_documents, { kind: '', reason: '' }] }))}>
        Add a document
      </Button>
      {routedKinds ? (
        <PolicyNote param={docRoutes}>Only {routedKinds.join(' and ')} have a verification route in this profile.</PolicyNote>
      ) : null}

      <SubHeading>Other affected units</SubHeading>
      <p className="mb-2 text-sm text-ink-soft">
        Units this work affects, or is affected by. Each one needs the agreement of that unit's responsible RS or a named deputy, with a current RS licence, before authorisation (CORE-047).
      </p>
      <FieldErrors errors={errors('affected_area_ids')} />
      {!area ? (
        <p className="text-sm text-ink-faint">Choose the unit first (step 1).</p>
      ) : otherAreas.length === 0 ? (
        <p className="text-sm text-ink-faint">This geographical area has no other units.</p>
      ) : (
        <div className="divide-y divide-line rounded-lg border border-line bg-white px-3">
          {otherAreas.map((a) => (
            <Checkbox
              key={a.id}
              checked={fields.affected_area_ids.includes(a.id)}
              onChange={(v) => update((f) => ({ ...f, affected_area_ids: toggleIn(f.affected_area_ids, a.id, v) }))}
              label={`${a.name} (${a.code})`}
            />
          ))}
        </div>
      )}

      <SubHeading>Area classification</SubHeading>
      <FieldErrors errors={[...errors('location_attributes_confirmed'), ...errors('area_classification')]} />
      <div className="mb-4 rounded-lg border border-line bg-white p-3 text-sm">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-faint">Unit's work-location classification (from the register)</p>
        {area ? (
          <p className="font-semibold text-ink">
            {area.location_classification_label ?? locationClassificationLabel(area.location_classification) ?? (
              <span className="text-bad">Not recorded - a permit cannot be submitted in this unit until it is.</span>
            )}
          </p>
        ) : (
          <p className="text-ink-faint">Choose the unit first (step 1).</p>
        )}
        <FieldErrors errors={errors('site_area_id').filter((x) => x.code === 'UNIT_NOT_CLASSIFIED')} />
      </div>
      {area ? (
        <div className="rounded-lg border-2 border-line-strong bg-white px-3">
          <Checkbox
            checked={fields.location_attributes_confirmed}
            onChange={(v) => update((f) => ({ ...f, location_attributes_confirmed: v }))}
            label={
              <>
                I confirm the unit's work-location classification shown applies at the worksite<span className="ml-1 text-bad" aria-hidden>*</span>
              </>
            }
            hint={`It is the classification at the worksite in ${area.name}, from the approved site record. Changing the unit clears this tick.`}
          />
        </div>
      ) : (
        <p className="text-sm text-ink-faint">Choose the unit first (step 1).</p>
      )}
      <p className="mt-2 text-xs text-ink-soft">The Responsible Supervisor reads this classification at the permit meeting and before authorising.</p>
    </div>
  )
}

/** A number of hours as typed (decimals allowed); null while empty or not a valid number. */
function HoursInput({ id, value, onChange }: { id: string; value: number | null; onChange: (v: number | null) => void }) {
  const [text, setText] = useState(value === null ? '' : String(value))
  const [touched, setTouched] = useState(false)
  useEffect(() => {
    // A value changed from outside (gas testing answered No, a reload) replaces the text.
    if (parseHoursInput(text) !== value) setText(value === null ? '' : String(value))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])
  const invalid = text.trim() !== '' && parseHoursInput(text) === null
  return (
    <>
      <div className="flex max-w-[16rem] items-center gap-2">
        <TextInput
          id={id}
          inputMode="decimal"
          autoComplete="off"
          value={text}
          aria-invalid={touched && invalid ? true : undefined}
          onChange={(e) => {
            setText(e.target.value)
            onChange(parseHoursInput(e.target.value))
          }}
          onBlur={() => setTouched(true)}
        />
        <span className="text-sm text-ink">hours</span>
      </div>
      {touched && invalid ? (
        <p className="mt-1 text-[13px] font-semibold text-bad">! Enter a number of hours greater than zero, for example 4 or 1.5.</p>
      ) : null}
    </>
  )
}

// -----------------------------------------------------------------------------
// Isolation details (0031): kind, reason, planned period, Isolation Certificate
// -----------------------------------------------------------------------------
function IsolationDetailsEditor({ id, fields, update, errors }: Pick<StepProps, 'fields' | 'update' | 'errors'> & { id: string }) {
  const iso = fields.isolation
  const setI = (patch: Partial<DraftFields['isolation']>) => update((f) => ({ ...f, isolation: { ...f.isolation, ...patch } }))
  const span = windowSpan(iso.planned_from, iso.planned_to)
  return (
    <div className="mt-3 rounded-lg border border-line bg-surface p-3">
      <FieldErrors errors={errors('isolation').filter((b) => !b.field || b.field === 'isolation')} />
      <Question
        legend="What kind of isolation?"
        required
        hint="Tick every kind the work needs. The Isolating Authority designs the points; the kind tells the RS what to expect."
        errors={errors('isolation').filter((b) => (b.field ?? '').startsWith('isolation.kinds'))}
      >
        <div className="divide-y divide-line rounded-lg border border-line bg-white px-3">
          {ISOLATION_KINDS.map((k) => (
            <Checkbox
              key={k.code}
              checked={iso.kinds.includes(k.code)}
              onChange={(v) => setI({ kinds: toggleIn(iso.kinds, k.code, v) })}
              label={
                <>
                  {k.label}
                  {k.help ? <span className="block text-xs text-ink-soft">{k.help}</span> : null}
                </>
              }
            />
          ))}
        </div>
      </Question>
      <Field
        label="What is isolated and why (reason for isolation)"
        required
        htmlFor={`${id}-iso-reason`}
        hint="The reason decides the level of isolation. Routine or breakdown maintenance alone is not acceptable (SP-2329 4.2.4)."
        error={fieldError(errors('isolation').filter((b) => b.field === 'isolation.reason'))}
      >
        <TextArea
          id={`${id}-iso-reason`}
          rows={2}
          value={iso.reason}
          onChange={(e) => setI({ reason: e.target.value })}
          placeholder="e.g. Isolate pump P-101 suction and discharge to replace the mechanical seal (breaking containment)"
        />
      </Field>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Field label="Isolation planned to be applied" required htmlFor={`${id}-iso-from`} error={fieldError(errors('isolation').filter((b) => b.field === 'isolation.planned_from'))}>
          <DateTimeInput id={`${id}-iso-from`} name="Isolation applied" value={toLocalInput(iso.planned_from)} onChange={(v) => setI({ planned_from: fromLocalInput(v) })} />
        </Field>
        <Field label="Isolation planned to be removed" required htmlFor={`${id}-iso-to`} error={fieldError(errors('isolation').filter((b) => b.field === 'isolation.planned_to'))}>
          <DateTimeInput id={`${id}-iso-to`} name="Isolation removed" value={toLocalInput(iso.planned_to)} onChange={(v) => setI({ planned_to: fromLocalInput(v) })} />
        </Field>
      </div>
      {span ? (
        <p className={span.ms > 0 ? 'mb-2 text-sm text-ink' : 'mb-2 text-sm font-semibold text-bad'}>
          {span.ms > 0 ? `Planned isolation duration: ${span.text}.` : '! The de-isolation must be after the isolation.'}
        </p>
      ) : null}
      <p className="mb-3 text-xs text-ink-soft">
        An isolation that must stay after this permit ends becomes an extended period isolation (EPI, form F09); the RS records the disposition at closeout.
        <RuleRefs ruleIds={['CORE-053', 'ACT-019']} />
      </p>
      <Question
        legend="Has the Isolation Certificate (form F08) been completed?"
        required
        hint="Not yet is allowed here: the certificate is raised after the application is signed. It must be recorded under Supporting documents and verified by the RS before the permit is authorised."
        errors={errors('isolation').filter((b) => b.field === 'isolation.certificate_documented')}
      >
        <YesNo
          name={`${id}-iso-cert`}
          value={iso.certificate_documented}
          onChange={(v) => setI(v ? { certificate_documented: true } : { certificate_documented: false, certificate_reference: null })}
          yesLabel="Yes - completed"
          noLabel="Not yet"
        />
      </Question>
      {iso.certificate_documented === true ? (
        <Field label="Isolation Certificate number" required htmlFor={`${id}-iso-ref`} hint="As written on the certificate." error={fieldError(errors('isolation').filter((b) => b.field === 'isolation.certificate_reference'))}>
          <TextInput id={`${id}-iso-ref`} value={iso.certificate_reference ?? ''} onChange={(e) => setI({ certificate_reference: e.target.value || null })} />
        </Field>
      ) : null}
      <p className="text-xs text-ink-soft">
        Recording the certificate is not verifying it, and verifying it is not the physical isolation. Before validation the RS confirms the isolation is in place.
        <RuleRefs ruleIds={['CORE-033', 'ACT-014']} />
      </p>
    </div>
  )
}
